import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import isEqual from 'lodash/isEqual';
import { useNavigate, useLocation } from 'react-router-dom';

import api from '../api';
import { loginAccount } from '../actions/login';
import { logoutAccount } from '../actions/logout';
import { forgotPassword, resetForgotPassword } from '../actions/forgotPassword';
import { setProfile } from '../actions/profile';
import { closeInviteModal, openInviteModal } from '../actions/invite';
import * as authActions from '../actions/auth';
import * as uiActions from '../actions/ui';
import * as pinActions from '../actions/pin';

import { getAuthenticatedAccountId, getAuthenticatedIsAdmin, getAuthenticatedIsStaff } from '../selectors/auth';
import { getEditingProfile, getIsEditingCurrentProfile, getIsPrimaryProfile, getSelectedProfile, getProfiles } from '../selectors/profiles';
import { getEditingBaby } from '../selectors/babies';
import { getRecentUnansweredQuestions } from '../selectors/questions';
import { getSignUp } from '../selectors/signUp';
import { getLogin } from '../selectors/login';
import { getForgotPassword } from '../selectors/forgotPassword';
import { getInvite } from '../selectors/invite';
import { getCurrentModuleWithLessonsAndProgress, getLessonProgressForCurrentProfile, getModuleProgressForCurrentProfile } from '../selectors/progress';
import { getLessons } from '../selectors/core';
import { getMenus } from '../selectors/menus';

import Header from '../components/Header/Header';

const EMPTY_ARRAY = [];

const HeaderContainer = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [duplicatedEmailError] = useState(false);

  const accountId = useSelector(getAuthenticatedAccountId);
  const token = useSelector(state => state.auth.token);
  const profile = useSelector(getSelectedProfile);
  const hasToken = useSelector(state => state.auth.hasToken);
  const hasProfile = useSelector(state => state.auth.hasProfile);
  const isAuthenticated = hasToken && hasProfile; 
  
  const profiles = useSelector(getProfiles, shallowEqual) || EMPTY_ARRAY;
  const questions = useSelector(getRecentUnansweredQuestions, shallowEqual) || EMPTY_ARRAY;
  const roles = useSelector(state => state.role?.all || state.rolesList?.data || EMPTY_ARRAY, shallowEqual);

  const uiState = useSelector((state) => ({
    isAdmin: getAuthenticatedIsAdmin(state),
    isStaff: getAuthenticatedIsStaff(state),
    menuShow: state.ui?.menuShow,
    isManagingProfiles: state.auth.isManagingProfiles,
    isProfileSelectorModalOpen: state.auth.isProfileSelectorModalOpen,
    isProfileEditorModalOpen: state.auth.isProfileEditorModalOpen,
    isEditingCurrentProfile: getIsEditingCurrentProfile(state),
    isLoginModalOpen: state.auth.isLoginModalOpen,
    isSignUpModalOpen: state.auth.isSignUpModalOpen,
    isForgotPasswordModalOpen: state.auth.isForgotPasswordModalOpen,
    isRequestSentModalOpen: state.auth.isRequestSentModalOpen,
    isInviteModalOpen: state.invites?.isInviteModalOpen,
    isBabyCreateModalOpen: state.ui?.babyCreateModalShown,
    
    isForgotPinModalOpen: state.pin?.isForgotPinModalOpen,
    isForgotPinLoading: state.pinForgot?.loading,
    isForgotPinSuccess: !!(state.pinForgot?.sync && !state.pinForgot?.error),
    isForgotPinFailure: !!state.pinForgot?.error,

    isLoadingProfiles: !state.profilesList?.sync || state.profilesList?.loading,
    editingProfile: getEditingProfile(state),
    editingBaby: getEditingBaby(state),
    signUpProps: getSignUp(state),
    loginProps: getLogin(state),
    forgotPasswordProps: getForgotPassword(state),
    inviteProps: getInvite(state),
    menuItems: state.menuList?.loading ? null : getMenus(state),
  }), shallowEqual);

  const locationContext = useMemo(() => ({ location }), [location]);
  const progressData = useSelector((state) => ({
    module: getCurrentModuleWithLessonsAndProgress(state, locationContext),
    lessons: getLessons(state, locationContext),
    moduleProgress: getModuleProgressForCurrentProfile(state),
    lessonProgress: getLessonProgressForCurrentProfile(state),
  }), isEqual);

  const profileId = profile?.id;
  const profileIdRef = useRef(profileId);
  useEffect(() => { profileIdRef.current = profileId; }, [profileId]);

  useEffect(() => {
    dispatch(api.actions.menuList()).catch(() => {});
  }, [dispatch]);

  useEffect(() => {
    if (accountId) {
      dispatch(api.actions.profilesList({ accountId })).catch(() => {});
    }
  }, [accountId, dispatch]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(api.actions.rolesList()).catch(() => {});
    }
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    if (accountId && profileId) {
      dispatch(api.actions.questionsList({ accountId, profileId })).catch(() => {});
    }
  }, [accountId, profileId, dispatch]);

  useEffect(() => {
    if (isAuthenticated) dispatch(api.actions.dischargeSteps({})).catch(() => {});
  }, [isAuthenticated, dispatch]);

  const handlers = useMemo(() => ({
    handleOpenLoginModal: () => dispatch(authActions.openLoginModal()),
    handleCloseLoginModal: () => dispatch(authActions.closeLoginModal()),
    handleLogout: () => dispatch(logoutAccount(token)),
    handleOpenSignUpModal: () => dispatch(authActions.openSignUpModal()),
    handleCloseSignUpModal: () => dispatch(authActions.closeSignUpModal()),
    handleOpenForgotPasswordModal: () => {
      dispatch(authActions.closeLoginModal());
      dispatch(authActions.openForgotPasswordModal());
    },
    handleCloseForgotPasswordModal: () => {
      dispatch(authActions.closeForgotPasswordModal());
      dispatch(resetForgotPassword());
    },
    handleForgotPassword: (values) => dispatch(forgotPassword((values?.email || '').trim())),
    handleSelectProfile: (id) => {
      dispatch(setProfile(id));
      navigate('/dashboard');
    },
    onChangeProfileClick: () => dispatch(authActions.openProfileSelectorModal()),
    handleCloseProfileSelectorModal: () => dispatch(authActions.closeProfileSelectorModal()),
    handleOpenProfileEditorModal: (id) => dispatch(authActions.openProfileEditorModal(id)),
    handleCloseProfileEditorModal: () => dispatch(authActions.closeProfileEditorModal()),
    handleEnterProfileManageMode: () => dispatch(authActions.enterProfileManageMode()),
    handleLeaveProfileManageMode: () => dispatch(authActions.leaveProfileManageMode()),
    handleDeleteProfile: (id) => dispatch(api.actions.profilesDelete({ accountId, profileId: id }))
      .then(() => {
        dispatch(authActions.closeProfileEditorModal());
        dispatch(api.actions.profilesList({ accountId }));
      }),
    handleInvite: (val) => dispatch(api.actions.invitesCreate({}, { body: JSON.stringify(val) })),
    
    handleCloseBabyEditorModal: () => {
      dispatch(uiActions.closeBabyEditorModal());
      dispatch(api.actions.babiesPatch.reset());
    },
    handleCloseBabyCreateModal: () => {
      dispatch(uiActions.closeBabyCreateModal());
      dispatch(api.actions.babiesPost.reset());
    },

    handleForgotPin: () => accountId && profileIdRef.current && dispatch(api.actions.pinForgot({}, { body: JSON.stringify({ accountId, profileId: profileIdRef.current }) })),
    handleCloseForgotPinModal: () => {
      dispatch(pinActions.closeForgotPinModal());
      dispatch(api.actions.pinForgot.reset());
    },

    handleOpenRequestSentModal: () => dispatch(authActions.openRequestSentModal()),
    handleCloseRequestSentModal: () => dispatch(authActions.closeRequestSentModal()),

    menuOpen: () => dispatch(uiActions.menuOpen()),
    menuClose: () => dispatch(uiActions.menuClose()),
  }), [dispatch, token, accountId, navigate]);

  return (
    <Header
      {...props}
      {...uiState}
      {...progressData}
      {...handlers}
      accountId={accountId}
      profile={profile}
      profiles={profiles}
      roles={roles}
      questions={questions}
      isAuthenticated={isAuthenticated}
      duplicatedEmailError={duplicatedEmailError}
    />
  );
};

export default HeaderContainer;